import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Plus, Search, Filter, RotateCcw, Edit, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'

interface Column {
  key: string
  label: string
  sortable?: boolean
}

interface AdminTableProps {
  title: string
  description: string
  data: any[]
  columns: Column[]
  pagination?: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
    prev_page?: number
    next_page?: number
  }
  filters?: {
    search?: string
    [key: string]: any
  }
  filter_options?: {
    [key: string]: any[]
  }
  can_edit: boolean
  onEdit?: (item: any) => void
  onDelete?: (item: any) => void
  onCreate?: (formData: any) => void
  searchPlaceholder?: string
  basePath: string
  formFields: Array<{
    name: string
    label: string
    type: 'text' | 'email' | 'select'
    options?: Array<{ value: string, label: string }>
    required?: boolean
  }>
}

export default function AdminTable({
  title,
  description,
  data,
  columns,
  pagination,
  filters = {},
  filter_options = {},
  can_edit,
  onEdit,
  onDelete,
  onCreate,
  searchPlaceholder = "Search...",
  basePath,
  formFields
}: AdminTableProps) {
  const [search, setSearch] = useState(filters.search || '')
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState(
    formFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {})
  )

  const handleFilter = (page = 1) => {
    router.get(basePath, {
      search: search,
      page: page,
      ...Object.fromEntries(
        Object.entries(filters).filter(([key]) => key !== 'search')
      )
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleReset = () => {
    setSearch('')
    router.get(basePath, {}, { preserveState: true })
  }

  const handleCreate = (e) => {
    e.preventDefault()
    if (onCreate) {
      onCreate(formData)
      setIsCreateDialogOpen(false)
      resetForm()
    }
  }

  const handleEdit = (item) => {
    setEditingItem(item)
    setFormData(
      formFields.reduce((acc, field) => ({ ...acc, [field.name]: item[field.name] || '' }), {})
    )
    setIsEditDialogOpen(true)
  }

  const handleEditSubmit = (e) => {
    e.preventDefault()
    if (onEdit && editingItem) {
      onEdit({ ...editingItem, ...formData })
      setIsEditDialogOpen(false)
      setEditingItem(null)
      resetForm()
    }
  }

  const resetForm = () => {
    setFormData(
      formFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {})
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
            <p className="mt-2 text-sm text-gray-600">{description}</p>
          </div>
          {can_edit && onCreate && (
            <div className="mt-4 sm:mt-0">
              <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
                if (!open) {
                  resetForm();
                }
                setIsCreateDialogOpen(open);
              }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                    onClick={() => {
                      resetForm();
                      setIsCreateDialogOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add New {title.slice(0, -1)}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Add New {title.slice(0, -1)}</DialogTitle>
                    <DialogDescription>
                      Create a new {title.slice(0, -1).toLowerCase()}.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    {formFields.map((field) => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                        </label>
                        {field.type === 'select' ? (
                          <Select
                            value={formData[field.name]}
                            onValueChange={(value) => setFormData({ ...formData, [field.name]: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={`Select ${field.label}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={field.type}
                            value={formData[field.name]}
                            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            required={field.required}
                          />
                        )}
                      </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit">Create</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>


      {/* Results Summary */}
      {pagination && (
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> to{' '}
            <span className="font-semibold">{Math.min(pagination.current_page * pagination.per_page, pagination.total_count)}</span> of{' '}
            <span className="font-semibold">{pagination.total_count}</span> results
          </p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.label}
                  </th>
                ))}
                {can_edit && (
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (can_edit ? 1 : 0)} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="rounded-full bg-gray-100 p-3 mb-4">
                        <Search className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm">No {title.toLowerCase()} found</p>
                      <p className="text-gray-400 text-xs mt-1">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    {columns.map((column) => (
                      <td key={column.key} className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                        {item[column.key]}
                      </td>
                    ))}
                    {can_edit && (
                      <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {onDelete && (
                            <button 
                              onClick={() => onDelete(item)}
                              className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Dialog */}
      {can_edit && onEdit && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Edit {title.slice(0, -1)}</DialogTitle>
              <DialogDescription>
                Update the {title.slice(0, -1).toLowerCase()} details.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {formFields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  {field.type === 'select' ? (
                    <Select
                      value={formData[field.name]}
                      onValueChange={(value) => setFormData({ ...formData, [field.name]: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${field.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={field.type}
                      value={formData[field.name]}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Update</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}